/**
 * The Console's data contract (§20.1).
 *
 * Every screen talks to this interface and never to `fetch` directly. That buys
 * two things worth the indirection:
 *
 *  - The read-only Office can be handed an object that *structurally has no
 *    mutation method* (see `office-client.ts`), which is how ADR-005 and AC-031
 *    become a property of the code rather than a rule people remember.
 *  - A deterministic in-memory source can drive every §16.3 state, so the state
 *    matrix is reviewable and testable without a control plane that happens to
 *    be having an outage at the right moment.
 */

import type { OfficeScene, StreamFrame } from '@autonomous-team/ui-projections';
import type { Proposal, ProposalDraft, ProposalKind, ProposalStatus } from '@autonomous-team/domain';

import type {
  HomeThreadMessage,
  TeamListItem,
  WorkflowView,
  WorkflowLayoutView,
  PackageOption,
  ApprovalReceiptView,
  ArtifactView,
  AuditEntry,
  BoardView,
  EpicDetailView,
  EpicView,
  CreateCardInput,
  SkillContentView,
  TeamVersionSummary,
  EvidenceView,
  ExecutionEnvironmentSummary,
  HarnessSummary,
  CredentialSummaryView,
  ManagedLoginView,
  ProviderLoginSessionView,
  ProviderConnectionInputView,
  ProviderConnectionView,
  WebhookBatchView,
  WebhookEndpointView,
  WebhookInputView,
  WebhookSecretView,
  GateView,
  IntegrationView,
  MainOperatorThread,
  OperatorChatSummary,
  OperatorChatThread,
  OperatorStatusView,
  HumanReviewRequestView,
  ReleaseOperationView,
  ReleasePageView,
  ReleasePlanView,
  ReleaseConfigurationView,
  RoleSummary,
  RunView,
  SceneSnapshot,
  SkillPackageView,
  TaskListItem,
  TaskSnapshotView,
  TeamStatus,
  TeamTemplate,
  ThreadView,
  ValidationFinding,
  ValidationReport,
  StageEntryNotificationView,
} from './types.js';

export interface Subscription {
  close(): void;
}

export interface SceneStreamHandlers {
  readonly onOpen: () => void;
  readonly onFrame: (frame: StreamFrame) => void;
  readonly onError: (message: string) => void;
}

/**
 * Read-only projection access.
 *
 * This is the whole surface the Virtual Office is given. It is declared
 * separately from `ConsoleDataSource` so that the Office's dependency cannot
 * widen by accident: adding a mutation to `ConsoleDataSource` does not add one
 * here, and the Office never names the wider type.
 */
export interface OfficeReadClient {
  readonly kind: 'live';
  readonly label: string;
  /** Atomic snapshot for a cold start or after a forced reset (ADR-107). */
  sceneSnapshot(): Promise<SceneSnapshot>;
  /** Cursored SSE subscription. `cursor` is the last frame the client rendered. */
  subscribeScene(cursor: string | null, handlers: SceneStreamHandlers): Subscription;
}

/** Identity and idempotency for every mutation (§20.1). */
/**
 * A file to send with a mutation.
 *
 * The browser's own `File`, not bytes the Console has read and re-encoded. It
 * used to be a base64 string, which meant every attachment was read into a
 * string, inflated by a third, and held in memory twice before it was sent
 * (ADR-119). `FormData` streams the file itself.
 */
export type UploadedFile = File;

export interface MutationContext {
  readonly idempotencyKey: string;
  readonly actorId: string;
}

export interface ConsoleDataSource extends OfficeReadClient {
  readonly description: string;

  teamStatus(): Promise<TeamStatus>;
  roles(): Promise<readonly RoleSummary[]>;
  roleThread(roleInstanceId: string): Promise<ThreadView>;
  tasks(): Promise<readonly TaskListItem[]>;
  task(taskBindingId: string): Promise<TaskSnapshotView>;
  /** The snapshot immediately before the current one, for TaskSnapshotDiff. */
  previousTaskSnapshot(taskBindingId: string): Promise<TaskSnapshotView | null>;
  runs(taskBindingId: string): Promise<readonly RunView[]>;
  checklists(taskBindingId: string): Promise<readonly import('./types.js').TaskChecklistView[]>;
  evidence(taskBindingId: string): Promise<readonly EvidenceView[]>;
  artifacts(taskBindingId: string): Promise<readonly ArtifactView[]>;
  /** The tail of the ledger, newest first. There is no per-task filter: the server has never had one. */
  audit(filter: { readonly limit?: number }): Promise<readonly AuditEntry[]>;
  /** The board, when this team owns one. Rejects with 404 otherwise. */
  board(epicId?: string, cursor?: string | null): Promise<BoardView>;
  epics(): Promise<readonly EpicView[]>;
  epic(epicId: string): Promise<EpicDetailView>;
  /** Deployment history, newest first, with API pagination metadata. */
  deployments(page: { readonly limit: number; readonly offset: number }): Promise<ReleasePageView>;
  deployment(deploymentId: string): Promise<ReleaseOperationView>;
  /** Production Release history, newest first, with API pagination metadata. */
  releases(page: { readonly limit: number; readonly offset: number }): Promise<ReleasePageView>;
  release(releaseId: string): Promise<ReleaseOperationView>;
  releasePlans(): Promise<readonly ReleasePlanView[]>;
  releasePlan(releaseId: string): Promise<ReleasePlanView>;
  releaseConfiguration(): Promise<ReleaseConfigurationView>;
  createReleasePlan(title: string, context: MutationContext, cards?: readonly string[]): Promise<ReleasePlanView['release']>;
  addReleaseTask(releaseId: string, card: string, context: MutationContext): Promise<void>;
  addReleaseTasks(releaseId: string, cards: readonly string[], context: MutationContext): Promise<void>;
  removeReleaseTask(releaseId: string, card: string, context: MutationContext): Promise<void>;
  deployRelease(releaseId: string, action: string, environment: string, context: MutationContext): Promise<void>;
  cancelReleaseRequest(releaseId: string, context: MutationContext): Promise<void>;
  settleReleasePlan(releaseId: string, status: 'CANCELLED', context: MutationContext): Promise<void>;
  deleteReleasePlan(releaseId: string, context: MutationContext): Promise<void>;
  /** Every saved configuration snapshot, newest first. */
  teamVersions(): Promise<readonly TeamVersionSummary[]>;
  skills(): Promise<readonly SkillPackageView[]>;
  /** Skill text, built-ins and overrides together. */
  skillContents(): Promise<readonly SkillContentView[]>;
  integrations(): Promise<readonly IntegrationView[]>;
  gates(): Promise<readonly GateView[]>;
  humanReviews(): Promise<readonly HumanReviewRequestView[]>;
  approvals(): Promise<readonly ApprovalReceiptView[]>;
  proposals(filter?: { readonly kind?: ProposalKind; readonly status?: ProposalStatus; readonly sourceTaskBindingId?: string }): Promise<readonly Proposal[]>;
  editProposal(proposalId: string, revision: number, draft: ProposalDraft, context: MutationContext): Promise<Proposal>;
  approveProposal(proposalId: string, revision: number, note: string, context: MutationContext): Promise<Proposal>;
  rejectProposal(proposalId: string, revision: number, note: string, context: MutationContext): Promise<Proposal>;
  templates(): Promise<readonly TeamTemplate[]>;
  mainOperatorThread(): Promise<MainOperatorThread>;

  /* -------------------------------------------- operator chats (v12) --- */

  /** `GET /v1/operator/status` — which program answers, and whether it is live. */
  operatorStatus(): Promise<OperatorStatusView>;
  /** `GET /v1/operator/chats` — conversations, newest first. */
  operatorChats(): Promise<readonly OperatorChatSummary[]>;
  createOperatorChat(context: MutationContext): Promise<OperatorChatSummary>;
  /** Lazily create the one Main Operator conversation owned by this task. */
  ensureTaskChat(taskBindingId: string, context: MutationContext): Promise<OperatorChatThread>;
  taskChat(taskBindingId: string, beforeSequence?: number): Promise<OperatorChatThread>;
  sendTaskChatMessage(
    taskBindingId: string,
    messageId: string,
    content: string,
    context: MutationContext,
    files?: readonly UploadedFile[],
  ): Promise<void>;
  retryTaskChatMessage(taskBindingId: string, messageId: string, context: MutationContext): Promise<void>;
  stopTaskChat(taskBindingId: string, context: MutationContext): Promise<void>;
  /** The one chat an agent has, found or started. */
  openRoleChat(roleId: string, context: MutationContext): Promise<OperatorChatSummary>;
  /** A disposable fork of the role's thread; throws with the reason when refused. */
  forkRoleChat(roleId: string, context: MutationContext): Promise<OperatorChatSummary>;
  /** The agent's working thread behind a role or fork chat. */
  homeThread(chatId: string): Promise<readonly HomeThreadMessage[]>;
  deleteOperatorChat(chatId: string, context: MutationContext): Promise<void>;
  operatorChat(chatId: string): Promise<OperatorChatThread>;
  /**
   * A message with whatever rides along from the picker. One request,
   * because it is one statement.
   */
  sendOperatorChatMessage(
    chatId: string,
    content: string,
    context: MutationContext,
    files?: readonly UploadedFile[],
  ): Promise<void>;
  /** Turns a microphone recording into editable text; the recording is not stored. */
  transcribeOperatorAudio(audio: File, context: MutationContext): Promise<string>;
  /**
   * The bytes behind an attachment digest, fetched with the session attached —
   * an `<img src>` cannot carry the header, so the Console builds object URLs.
   */
  attachmentBlob(digest: string): Promise<Blob>;
  /** The stored value of one credential, fetched per click, never cached. */
  credentialValue(name: string): Promise<string>;
  /** Durable MCP credential, fetched only after Console authentication. */
  mcpAccessToken(context: MutationContext): Promise<{ readonly token: string; readonly persistent: boolean }>;
  /** Reset one conversation's model context; home and history stay. */
  resetOperatorChat(chatId: string, context: MutationContext): Promise<void>;
  /** Stop the reply being produced in one conversation; history stays. */
  stopOperatorChat(chatId: string, context: MutationContext): Promise<void>;
  /** Who reads a skill: an empty list restores «every agent». */
  setSkillRoles(ref: string, roles: readonly string[], context: MutationContext): Promise<void>;
  /**
   * The exact bytes a version was approved as.
   *
   * The editor works on the real document rather than on a model of its own,
   * so a field the compiler understands and the editor does not is preserved
   * instead of silently dropped on the next save.
   */
  teamVersionDocument(teamId: string, version: number): Promise<string | null>;
  /**
   * `GET /v1/environments` — machines an agent can be placed on.
   *
   * Read from the execution plane on each call rather than cached, because
   * pairing a machine happens in Orca and a list captured when the Console
   * loaded would quietly omit whatever was added since.
   */
  environments(): Promise<readonly ExecutionEnvironmentSummary[]>;
  /** `GET /v1/harnesses` — agent programs a role may run, with their caveats. */
  harnesses(): Promise<readonly HarnessSummary[]>;
  /** Every team on this control plane, for the switcher. */
  listTeams(): Promise<readonly TeamListItem[]>;
  /** Create-and-start a team from the delivery template. */
  bootstrapTeam(slug: string, displayName: string, context: MutationContext): Promise<TeamListItem>;
  /** `GET /v1/teams/{id}/workflow` — columns, and where each stage's claim goes. */
  teamWorkflow(teamId: string): Promise<WorkflowView>;
  workflowLayout(teamId: string, topologySignature: string): Promise<WorkflowLayoutView | null>;
  saveWorkflowLayout(
    teamId: string,
    input: Pick<WorkflowLayoutView, 'topologySignature' | 'positions' | 'waypoints'> & { readonly expectedRevision: number },
    context: MutationContext,
  ): Promise<WorkflowLayoutView>;
  /** `GET /v1/packages` — professions a role can have, with goals and stages. */
  packages(): Promise<readonly PackageOption[]>;
  /** `GET /v1/credentials` — names and grants. Values are never returned. */
  credentials(): Promise<readonly CredentialSummaryView[]>;
  webhooks(teamId?: string): Promise<readonly WebhookEndpointView[]>;
  webhookBatches(endpointId: string, teamId?: string): Promise<readonly WebhookBatchView[]>;
  createWebhook(input: WebhookInputView, context: MutationContext): Promise<WebhookSecretView>;
  updateWebhook(
    endpointId: string,
    input: Partial<Omit<WebhookInputView, 'authMode'>>,
    context: MutationContext,
  ): Promise<WebhookEndpointView>;
  rotateWebhookSecret(
    endpointId: string,
    input: { readonly authMode: WebhookInputView['authMode']; readonly secret?: string },
    context: MutationContext,
  ): Promise<WebhookSecretView>;
  retryWebhookBatch(endpointId: string, batchId: string, context: MutationContext): Promise<void>;
  /** `PUT /v1/credentials/{name}` — sets a value and replaces its grants. */
  putCredential(
    name: string,
    input: { readonly value: string; readonly description: string | null; readonly roles: readonly string[] },
    context: MutationContext,
  ): Promise<CredentialSummaryView>;
  /** `PUT /v1/credentials/{name}/roles` — ticks boxes without re-typing a secret. */
  setCredentialRoles(name: string, roles: readonly string[], context: MutationContext): Promise<CredentialSummaryView>;
  /** `DELETE /v1/credentials/{name}` — deleting what is not there is not an error. */
  removeCredential(name: string, context: MutationContext): Promise<void>;

  /* ------------------------------------ AI provider connections (ADR-131) --- */

  /** `GET /v1/provider-connections` — accounts, who uses them, what they spent. */
  providerConnections(): Promise<readonly ProviderConnectionView[]>;
  stageEntryNotifications(): Promise<readonly StageEntryNotificationView[]>;
  /** `GET /v1/provider-connections/managed-logins` — logins this machine has. */
  managedLogins(): Promise<readonly ManagedLoginView[]>;
  startProviderLogin(provider: 'claude' | 'codex' | 'grok', context: MutationContext): Promise<ProviderLoginSessionView>;
  providerLoginStatus(id: string): Promise<ProviderLoginSessionView>;
  writeProviderLogin(id: string, input: string, context: MutationContext): Promise<ProviderLoginSessionView>;
  cancelProviderLogin(id: string, context: MutationContext): Promise<void>;
  /** `POST /v1/provider-connections` — the one call a key ever travels on. */
  createProviderConnection(
    input: ProviderConnectionInputView,
    context: MutationContext,
  ): Promise<ProviderConnectionView>;
  /** `PUT /v1/provider-connections/{id}` — an absent key leaves the stored one. */
  updateProviderConnection(
    id: string,
    input: Partial<ProviderConnectionInputView>,
    context: MutationContext,
  ): Promise<ProviderConnectionView>;
  /** `POST /v1/provider-connections/{id}/verify` — look once, record what was seen. */
  verifyProviderConnection(id: string, context: MutationContext): Promise<ProviderConnectionView>;
  /** `DELETE /v1/provider-connections/{id}` — refused while assigned or busy. */
  removeProviderConnection(id: string, context: MutationContext): Promise<void>;
  /** `POST /v1/teams` — a new team row; versions and activation come after. */
  createTeam(slug: string, displayName: string, context: MutationContext): Promise<{ readonly id: string }>;
  /** `POST /v1/teams/validate` — never has side effects (AC-001). */
  validateTeam(document: string, context: MutationContext): Promise<ValidationReport>;
  /** Drafts and validates a new version. Returns the errors when it will not compile. */
  draftTeamVersion(
    teamId: string,
    document: string,
    context: MutationContext,
    baseVersion?: number,
  ): Promise<{ readonly drafted: boolean; readonly version?: number; readonly errors?: readonly ValidationFinding[] }>;
  activateTeamVersion(teamId: string, version: number, context: MutationContext): Promise<void>;
  saveSkill(ref: string, name: string, text: string, context: MutationContext, enabled?: boolean): Promise<void>;
  /** Removes an override, restoring the built-in when there is one. */
  resetSkill(ref: string, context: MutationContext): Promise<void>;
  createCard(input: CreateCardInput, context: MutationContext): Promise<void>;
  createEpic(title: string, brief: string, context: MutationContext): Promise<EpicView>;
  updateEpic(epicId: string, patch: { readonly brief?: string; readonly state?: EpicView['state'] }, context: MutationContext): Promise<EpicView>;
  deleteEpic(epicId: string, context: MutationContext): Promise<import('./types.js').DeleteEpicResult>;
  answerEpicQuestion(epicId: string, questionId: string, answer: string, context: MutationContext): Promise<void>;
  attachEpicTask(epicId: string, externalItemId: string, context: MutationContext): Promise<void>;
  detachEpicTask(epicId: string, externalItemId: string, context: MutationContext): Promise<void>;
  setTaskDependency(epicId: string, taskId: string, blockedBy: string, context: MutationContext): Promise<void>;
  removeTaskDependency(epicId: string, taskId: string, blockedBy: string, context: MutationContext): Promise<void>;
  editCard(externalItemId: string, patch: Partial<CreateCardInput>, context: MutationContext): Promise<void>;
  /**
   * A comment and the files that came with it, in one call.
   *
   * One request because it is one statement: uploading first and commenting
   * second leaves a window in which the screenshots exist and the sentence
   * explaining them does not.
   */
  commentOnCard(
    externalItemId: string,
    body: string,
    context: MutationContext,
    attachments?: readonly UploadedFile[],
  ): Promise<void>;
  /** Attaches a file to the card itself, with no comment of its own. */
  attachToCard(externalItemId: string, input: UploadedFile, context: MutationContext): Promise<void>;
  pauseRole(roleInstanceId: string, context: MutationContext): Promise<void>;
  resumeRole(roleInstanceId: string, context: MutationContext): Promise<void>;
  /** The whole team at once: nothing schedules, nothing dispatches, in-flight work finishes. */
  pauseTeam(context: MutationContext): Promise<void>;
  resumeTeam(context: MutationContext): Promise<void>;
  /** Reusable quorums (spec §5): list, versions, recent runs, and management. */
  quorums(): Promise<readonly {
    readonly id: string;
    readonly slug: string;
    readonly displayName: string;
    readonly activeVersion: number | null;
    readonly versions: readonly { readonly version: number; readonly state: string; readonly note: string | null }[];
  }[]>;
  quorumVersions(quorumId: string): Promise<readonly { readonly version: number; readonly state: string; readonly definition: string; readonly note: string | null }[]>;
  quorumRuns(): Promise<readonly {
    readonly id: string;
    readonly quorumId: string;
    readonly quorumVersion: number;
    readonly state: string;
    readonly aggregate: string | null;
    readonly createdAt: string;
    readonly participants: readonly { readonly model: string; readonly harness: string; readonly state: string; readonly verdict: string | null }[];
  }[]>;
  createQuorum(slug: string, displayName: string, context: MutationContext): Promise<void>;
  /** Drafts a version and returns its number, so the caller can activate it in one flow. */
  draftQuorumVersion(quorumId: string, definition: string, note: string, context: MutationContext): Promise<number>;
  activateQuorumVersion(quorumId: string, version: number, context: MutationContext): Promise<void>;
  /** Token spend by model and role (spec §6), optionally windowed. */
  usage(range?: { readonly from?: string; readonly to?: string }): Promise<
    readonly {
      readonly role: string;
      readonly model: string;
      readonly attempts: number;
      readonly inputTokens: number;
      readonly outputTokens: number;
      readonly providerCalls: number;
      readonly computeMs: number;
      readonly costMicros: number;
      readonly exact: number;
      readonly unknown: number;
    }[]
  >;
  /** The same table as a downloadable text (csv | json). */
  usageExport(format: 'csv' | 'json', range?: { readonly from?: string; readonly to?: string }): Promise<string>;
  /** Per-card spend and outcome counters (spec §6.4). */
  taskUsage(taskBindingId: string): Promise<{
    readonly attempts: number;
    readonly failed: number;
    readonly stopped: number;
    readonly inputTokens: number;
    readonly outputTokens: number;
    readonly computeMs: number;
    readonly costMicros: number;
    readonly byRole: readonly { readonly roleInstanceId: string; readonly model: string; readonly inputTokens: number; readonly outputTokens: number; readonly attempts: number }[];
  }>;
  /** Stop now (spec §3): the Ctrl-C — kills live attempts, stays down until Resume. */
  stopRole(roleInstanceId: string, reason: string, context: MutationContext): Promise<void>;
  stopTeam(reason: string, context: MutationContext): Promise<void>;
  sendMainOperatorMessage(body: string, context: MutationContext): Promise<void>;
  recordHumanDecision(
    taskBindingId: string,
    decisionId: string,
    answer: string,
    context: MutationContext,
  ): Promise<void>;
  retryStage(stageRunId: string, context: MutationContext): Promise<void>;
  cancelRun(runId: string, context: MutationContext): Promise<void>;
}

/**
 * Runtime shape check for a scene arriving over the wire.
 *
 * Deliberately shallow: it confirms the envelope the renderer relies on rather
 * than re-validating the projection, because re-deriving the projection's rules
 * here would be the duplication §16.4 warns about — the canvas and the Truth
 * View must render one state, not two interpretations of it.
 */
export function decodeScene(payload: unknown): OfficeScene | null {
  if (payload === null || typeof payload !== 'object') return null;
  const candidate = payload as Partial<OfficeScene>;
  if (typeof candidate.generatedAt !== 'string') return null;
  if (candidate.mode !== 'live' && candidate.mode !== 'frozen') return null;
  if (!Array.isArray(candidate.agents) || !Array.isArray(candidate.tasks)) return null;
  if (!Array.isArray(candidate.decorations) || !Array.isArray(candidate.warnings)) return null;
  if (candidate.sourceFreshness === undefined || candidate.sourceFreshness === null) return null;
  return candidate as OfficeScene;
}

/** Generates an idempotency key. `crypto.randomUUID` is required by §20.1's contract. */
export function mutationContext(actorId: string): MutationContext {
  return { idempotencyKey: crypto.randomUUID(), actorId };
}
